import { useCallback, useEffect, useMemo, useState } from 'react';
import { transformDataFromHoaDon, transformDataFromHoaDonDichVu, type HoaDonSourceData } from '../utils/transform-data';
import { GiayBaoNo, GiayBaoNoInput, type TaiKhoan } from '@/types/schemas';
import { useRouter, useSearchParams } from '@/routing';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
import { useCRUD } from '@/hooks';
import api from '@/lib/api';

export function useInvoiceParamsPresence() {
  const searchParams = useSearchParams();
  return useMemo(() => {
    const hasInvoiceParams = Boolean(searchParams.get('hdmh') || searchParams.get('hdmdv'));
    const hasEditParam = Boolean(searchParams.get('edit'));
    return hasInvoiceParams && !hasEditParam;
  }, [searchParams]);
}

interface UseMultipleFormsArgs {
  addGiayBaoNo: (data: GiayBaoNoInput) => Promise<unknown>;
}

export function useMultipleForms({ addGiayBaoNo }: UseMultipleFormsArgs) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { entity } = useAuth();

  const hasInvoiceParams = useInvoiceParamsPresence();

  const [invoiceData, setInvoiceData] = useState<HoaDonSourceData[]>([]);
  const [showMultipleForms, setShowMultipleForms] = useState<boolean>(hasInvoiceParams);
  const [currentFormIndex, setCurrentFormIndex] = useState<number>(0);

  const taiKhoanSearchData = useMemo(() => ({ prefix: '1121' }), []);
  const { data: taiKhoan, isLoading: isLoadingTaiKhoan } = useCRUD<TaiKhoan, unknown>({
    endpoint: QUERY_KEYS.TAI_KHOAN,
    searchData: taiKhoanSearchData
  });

  const fetchInvoiceByUuid = useCallback(
    async (uuid: string, isHoaDonMuaHang: boolean = true): Promise<HoaDonSourceData | null> => {
      if (!entity?.slug) return null;

      try {
        const cacheKey = `hoa_don_detail_${uuid}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
          sessionStorage.removeItem(cacheKey);
          return JSON.parse(cachedData) as HoaDonSourceData;
        }
        const endpoint = isHoaDonMuaHang ? QUERY_KEYS.HOA_DON_MUA_HANG_TRONG_NUOC : QUERY_KEYS.HOA_DON_MUA_DICH_VU;
        const response = await api.get(`/entities/${entity.slug}/erp/${endpoint}/${uuid}/`);
        return response.data as HoaDonSourceData;
      } catch (error) {
        return null;
      }
    },
    [entity?.slug]
  );

  useEffect(() => {
    const hdmhParam = searchParams.get('hdmh');
    const hdmdvParam = searchParams.get('hdmdv');
    const editParam = searchParams.get('edit');

    if (hdmhParam || hdmdvParam) {
      if (editParam) {
        setShowMultipleForms(false);
      } else {
        setShowMultipleForms(true);

        const invoiceUuids = [];
        if (hdmhParam) invoiceUuids.push({ uuid: hdmhParam, isHoaDonMuaHang: true });
        if (hdmdvParam) invoiceUuids.push({ uuid: hdmdvParam, isHoaDonMuaHang: false });

        if (invoiceUuids.length > 0) {
          Promise.all(invoiceUuids.map(({ uuid, isHoaDonMuaHang }) => fetchInvoiceByUuid(uuid, isHoaDonMuaHang)))
            .then(invoices => {
              const validInvoices = invoices.filter((invoice): invoice is HoaDonSourceData => Boolean(invoice));
              setInvoiceData(validInvoices);
              if (validInvoices.length > 0) {
                setCurrentFormIndex(0);
              }
            })
            .catch(() => {
              // Handle error silently, user can still use the form manually
            });
        }
      }
    }
  }, [searchParams, entity?.slug, fetchInvoiceByUuid]);

  const initialData = useMemo(() => {
    const currentInvoice = invoiceData[currentFormIndex];
    if (!currentInvoice) return {};

    const hdmhUuid = searchParams.get('hdmh');
    const hdmdvUuid = searchParams.get('hdmdv');

    let baseData: Partial<GiayBaoNo> = {};
    if (hdmhUuid && currentInvoice.uuid === hdmhUuid) {
      baseData = transformDataFromHoaDon(currentInvoice);
    } else if (hdmdvUuid && currentInvoice.uuid === hdmdvUuid) {
      baseData = transformDataFromHoaDonDichVu(currentInvoice);
    }

    if (!isLoadingTaiKhoan && taiKhoan && taiKhoan.length > 0) {
      return {
        ...baseData,
        tk_data: taiKhoan[0]
      };
    }

    return baseData;
  }, [invoiceData, currentFormIndex, searchParams, isLoadingTaiKhoan, taiKhoan]);

  const handleMultipleFormSubmit = useCallback(
    async (data: GiayBaoNoInput) => {
      try {
        await addGiayBaoNo(data);
        if (currentFormIndex < invoiceData.length - 1) {
          setShowMultipleForms(true);
          setCurrentFormIndex(currentFormIndex + 1);
        } else {
          setShowMultipleForms(false);
          setInvoiceData([]);
          setCurrentFormIndex(0);
          router.back();
        }
      } catch (error) {
        return;
      }
    },
    [addGiayBaoNo, currentFormIndex, invoiceData.length, router]
  );

  const handleCloseMultipleForms = useCallback(() => {
    setShowMultipleForms(false);
    setInvoiceData([]);
    setCurrentFormIndex(0);
    router.back();
  }, [router]);

  return {
    hasInvoiceParams,
    showMultipleForms,
    invoiceData,
    currentFormIndex,
    initialData,
    handleMultipleFormSubmit,
    handleCloseMultipleForms
  } as const;
}
